import { Dialog, DialogContent, DialogTitle, Box, Typography, IconButton, Chip, Button, Grid, TextField, MenuItem, Alert, CircularProgress } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import { getJobById } from '../Api/JobsServiceApi';
import { GetBenifits, searchBenefitsApi } from '../Api/Benifitsapi';
import { getAllSkills, searchSkillsApi } from '../Api/SkillsApi';

// Normalize raw job API response → flat fields for the dialog
// Confirmed: Sequelize serializes no-alias hasMany as "job_locations" / "job_benefits"
const normalizeJob = (job) => {
    if (!job) return {};

    // ── Resolve location ─────────────────────────────────────────────────
    const locations =
        job.job_locations  ||   // ✅ confirmed key name
        job.JobLocations   ||
        job.jobLocations   ||
        [];

    const primaryLocation = Array.isArray(locations) && locations.length > 0
        ? locations[0]
        : null;

    // Fallback: parse the "locationSummary" field ("City, Area") when job_locations is empty
    let city = primaryLocation?.city    || job.city    || '';
    let area = primaryLocation?.area    || job.area    || '';
    const pinCode = primaryLocation?.pincode || primaryLocation?.pinCode || job.pinCode || '';

    if (!city && job.locationSummary) {
        const parts = String(job.locationSummary).split(',').map(s => s.trim());
        city = parts[0] || '';
        area = parts[1] || '';
    }

    // ── Resolve benefits ─────────────────────────────────────────────────
    const benefitsArray =
        job.job_benefits   ||   // ✅ confirmed key name
        job.JobBenefits    ||
        job.jobBenefits    ||
        [];

    const rawBenefits = Array.isArray(benefitsArray)
        ? benefitsArray.map(b => (typeof b === 'string' ? { id: b, name: b } : { id: b.benefitId || b.id, name: b.name || '' })).filter(b => b.name)
        : Array.isArray(job.benefits)
        ? job.benefits.map(b => (typeof b === 'string' ? { id: b, name: b } : { id: b.benefitId || b.id || b.benefitId, name: b.name || '' })).filter(b => b.name)
        : [];

    // ── Resolve skills ───────────────────────────────────────────────────
    const skillsArray =
        job.job_skills     ||
        job.JobSkills      ||
        job.jobSkills      ||
        [];

    const rawSkills = Array.isArray(skillsArray)
        ? skillsArray.map(s => {
            const skillObj = s.skill || s;
            return {
                id: skillObj.id || s.skillId || s.id || '',
                name: skillObj.name || skillObj.skillName || ''
            };
        }).filter(s => s.name)
        : Array.isArray(job.skills)
        ? job.skills.map(s => {
            const skillObj = s.skill || s;
            return {
                id: skillObj.id || s.skillId || s.id || '',
                name: skillObj.name || skillObj.skillName || ''
            };
        }).filter(s => s.name)
        : [];

    // ── Map backend field names → frontend display names ─────────────────
    const jobType =
        job.jobType ||
        (job.employmentType === 'full_time'  ? 'Full Time'  :
         job.employmentType === 'part_time'  ? 'Part Time'  :
         job.employmentType === 'contract'   ? 'Contract'   :
         job.employmentType === 'internship' ? 'Internship' : job.employmentType) || '';

    const payMaximum = job.payMaximum ?? job.maxSalary ?? '';
    const payPeriod  =
        job.payPeriod ||
        (job.salaryPeriod === 'per_month' ? 'Per Month' :
         job.salaryPeriod === 'per_week'  ? 'Per Week'  :
         job.salaryPeriod === 'per_annum' ? 'Per Year'  :
         job.salaryPeriod === 'per_hour'  ? 'Per Hour'  : job.salaryPeriod) || '';

    const numberOfPeople = job.numberOfPeople ?? job.positions ?? '';

    return {
        ...job,
        city,
        area,
        pinCode,
        benefits:      rawBenefits,
        skills:        rawSkills,
        jobType,
        payMaximum,
        payPeriod,
        numberOfPeople,
    };
};



const PreviewDialogBox = ({
    open,
    onClose,
    jobId,                          // NEW – used to self-fetch
    formData: propFormData = {},    // fallback / initial data from parent
    selectedBenefits = [],
    benefits = [],
    handleBenefitToggle,
    selectedSkills = [],
    skills = [],
    handleSkillToggle,
    onSkillsChange = () => { },
    handleSubmit,
    isSubmitting = false,
    isEditMode = false,
    onFieldChange = () => { },
    onBenefitsChange = () => { },
    updateError = '',
    updateSuccess = '',
    hasChanges = false,
}) => {
    const BRAND = '#7B3AB7';

    const [editingState, setEditingState] = useState({
        description: false,
        benefits: false,
        skills: false,
        details: false,
    });
    const editorRef = useRef(null);

    // Internal job data state – populated by self-fetch
    const [fetchedData, setFetchedData]       = useState(null);
    const [fetchLoading, setFetchLoading]     = useState(false);
    const [fetchError, setFetchError]         = useState('');

    // Autocomplete search states
    const [allBenefits, setAllBenefits] = useState([]);
    const [benefitsInputValue, setBenefitsInputValue] = useState('');
    const [loadingBenefits, setLoadingBenefits] = useState(false);

    const [allSkills, setAllSkills] = useState([]);
    const [skillsInputValue, setSkillsInputValue] = useState('');
    const [loadingSkills, setLoadingSkills] = useState(false);

    // The data actually shown in the dialog
    const formData = fetchedData || propFormData;
    const displayBenefits =
        fetchedData?.benefits?.length > 0 ? fetchedData.benefits : selectedBenefits;
    const displaySkills =
        fetchedData?.skills?.length > 0 ? fetchedData.skills : selectedSkills;

    // ── Self-fetch when dialog opens ──────────────────────────────────────
    useEffect(() => {
        if (!open) return;

        // Reset UI state
        setEditingState({ description: false, benefits: false, skills: false, details: false });
        setFetchedData(null);
        setFetchError('');
        setBenefitsInputValue('');
        setSkillsInputValue('');

        const id = jobId ?? propFormData?.id;
        if (!id) {
            return;
        }

        let cancelled = false;
        const fetchData = async () => {
            setFetchLoading(true);
            try {
                const response = await getJobById(id);
                if (cancelled) return;
                const raw = response?.data ?? response;
                const normalized = normalizeJob(raw);
                setFetchedData(normalized);
                
                // Inform parent about normalized state
                if (normalized.benefits) onBenefitsChange(normalized.benefits);
                if (normalized.skills) onSkillsChange(normalized.skills);
            } catch (err) {
                if (cancelled) return;
                console.error('PreviewDialogBox: failed to fetch job', err);
                setFetchError('Could not load full job details.');
            } finally {
                if (!cancelled) setFetchLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, jobId]);

    // Debounced search for skills
    useEffect(() => {
        if (!open) return;

        const handler = setTimeout(async () => {
            if (skillsInputValue.trim() !== '') {
                setLoadingSkills(true);
                try {
                    const res = await searchSkillsApi(skillsInputValue);
                    if (res?.success) {
                        setAllSkills(res.data || []);
                    }
                } catch (error) {
                    console.error("Failed to search skills", error);
                } finally {
                    setLoadingSkills(false);
                }
            } else {
                try {
                    const res = await getAllSkills(1000);
                    if (res?.success) {
                        setAllSkills(res.data || []);
                    }
                } catch (error) {
                    setAllSkills([]);
                }
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [skillsInputValue, open]);

    // Debounced search for benefits
    useEffect(() => {
        if (!open) return;

        const handler = setTimeout(async () => {
            if (benefitsInputValue.trim() !== '') {
                setLoadingBenefits(true);
                try {
                    const res = await searchBenefitsApi(benefitsInputValue);
                    if (res?.success) {
                        setAllBenefits(res.data || []);
                    }
                } catch (error) {
                    console.error("Failed to search benefits", error);
                } finally {
                    setLoadingBenefits(false);
                }
            } else {
                try {
                    const benefitsRes = await GetBenifits();
                    const rawBenefits = Array.isArray(benefitsRes) ? benefitsRes : benefitsRes?.data || [];
                    setAllBenefits(rawBenefits.map(b => typeof b === 'string' ? { id: b, name: b } : b));
                } catch (error) {
                    setAllBenefits([]);
                }
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [benefitsInputValue, open]);

    const handleLocalBenefitToggle = (benefit) => {
        if (handleBenefitToggle) {
            handleBenefitToggle(benefit);
        }
        if (fetchedData) {
            setFetchedData(prev => {
                const exists = (prev.benefits || []).some(b => b.id === benefit.id);
                const updatedBenefits = exists
                    ? (prev.benefits || []).filter(b => b.id !== benefit.id)
                    : [...(prev.benefits || []), benefit];
                return { ...prev, benefits: updatedBenefits };
            });
        }
    };

    const handleLocalSkillToggle = (skill) => {
        if (handleSkillToggle) {
            handleSkillToggle(skill);
        }
        if (fetchedData) {
            setFetchedData(prev => {
                const exists = (prev.skills || []).some(s => s.id === skill.id);
                const updatedSkills = exists
                    ? (prev.skills || []).filter(s => s.id !== skill.id)
                    : [...(prev.skills || []), skill];
                return { ...prev, skills: updatedSkills };
            });
        }
    };

    // ── Rich text editor ──────────────────────────────────────────────────
    const applyFormat = (command, value = null) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
    };
    const handleBold        = () => applyFormat('bold');
    const handleItalic      = () => applyFormat('italic');
    const handleUnderline   = () => applyFormat('underline');
    const handleStrike      = () => applyFormat('strikeThrough');
    const handleBulletList  = () => applyFormat('insertUnorderedList');
    const handleNumberList  = () => applyFormat('insertOrderedList');
    const handleSubscript   = () => applyFormat('subscript');
    const handleSuperscript = () => applyFormat('superscript');
    const handleAlignLeft   = () => applyFormat('justifyLeft');
    const handleAlignCenter = () => applyFormat('justifyCenter');
    const handleAlignRight  = () => applyFormat('justifyRight');

    // Sync description HTML into editor when edit mode is toggled on
    useEffect(() => {
        if (editingState.description && editorRef.current) {
            if (editorRef.current.innerHTML !== (formData.description || '')) {
                editorRef.current.innerHTML = formData.description || '';
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingState.description]);

    const toggleSection = (section) => {
        setEditingState((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const handleDescriptionChange = () => {
        if (editorRef.current) {
            const html = editorRef.current.innerHTML;
            onFieldChange('description', html);
            setFetchedData(prev => prev ? { ...prev, description: html } : prev);
        }
    };

    const handleDetailChange = (field) => (event) => {
        const value = event.target.value;
        onFieldChange(field, value);
        setFetchedData(prev => prev ? { ...prev, [field]: value } : prev);
    };

    const detailFields = [
        { label: 'Job Title',              field: 'title' },
        { label: 'City',                   field: 'city' },
        { label: 'Area',                   field: 'area' },
        { label: 'Pin Code',               field: 'pinCode',            type: 'number' },
        { label: 'Recruitment Timeline',   field: 'recruitmentTimeline' },
        { label: 'No of Openings',         field: 'numberOfPeople',     type: 'number' },
        { label: 'Job Type',               field: 'jobType',            type: 'select', options: ['Full Time', 'Part Time', 'Contract', 'Internship'] },
        { label: 'Pay (Maximum)',          field: 'payMaximum',         type: 'number' },
        { label: 'Pay Period',             field: 'payPeriod',          type: 'select', options: ['Per Month', 'Per Week', 'Per Day', 'Per Year'] },
    ];


    return (
        <Dialog open={open} onClose={onClose} maxWidth="100%" fullWidth
            PaperProps={{ sx: { borderRadius: 10, maxHeight: '90vh' } }}
        >
            <DialogTitle sx={{
                backgroundColor: '#7B3AB7', color: 'white',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                p: 2, px: 5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RemoveRedEyeOutlinedIcon />
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Preview</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white', borderRadius: '10px' }}>
                    <CloseIcon sx={{ fontSize: '14px', p: 0.3, color: 'red', backgroundColor: 'white', borderRadius: '50%' }} />
                    <Typography sx={{ fontSize: '18px', pl: 1, fontWeight: 300 }}>Close</Typography>
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 4, py: 2 }}>

                    {/* Loading indicator */}
                    {fetchLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CircularProgress size={18} sx={{ color: '#7B3AB7' }} />
                            <Typography sx={{ fontSize: '14px', color: '#7B3AB7' }}>Loading job details...</Typography>
                        </Box>
                    )}

                    {fetchError && (
                        <Alert severity="warning" sx={{ mb: 2 }}>{fetchError}</Alert>
                    )}

                    {/* Job Title, Location, Date */}
                    <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessCenterOutlinedIcon sx={{ fontSize: 20 }} />
                            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
                                {formData.title || 'Job Title'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOnOutlinedIcon sx={{ fontSize: 20 }} />
                            <Typography sx={{ fontSize: '16px' }}>
                                {formData.city || 'City'}, {formData.area || 'Area'} {formData.pinCode || ''}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />
                            <Typography sx={{ fontSize: '16px' }}>
                                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </Typography>
                        </Box>
                    </Box>

                    <Grid container spacing={4}>
                        {/* ── Left – Description & Benefits ── */}
                        <Grid size={{ xs: 12, md: 7 }}>
                            {/* Job Description */}
                            <Box sx={{ backgroundColor: '#F5F5FF', p: 2, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ color: '#7B3AB7', fontWeight: 600 }}>
                                        JOB DESCRIPTION
                                    </Typography>
                                    {isEditMode && (
                                        <IconButton size="small" onClick={() => toggleSection('description')}>
                                            <EditOutlinedIcon sx={{ color: '#FF0000', fontSize: 18 }} />
                                        </IconButton>
                                    )}
                                </Box>

                                {editingState.description ? (
                                    <Box sx={{ border: '1px solid #ddd', borderRadius: 2, overflow: 'hidden', backgroundColor: 'white', mt: 1 }}>
                                        {/* Editable area */}
                                        <Box
                                            ref={editorRef}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={handleDescriptionChange}
                                            sx={{
                                                p: 2,
                                                minHeight: '200px',
                                                maxHeight: '320px',
                                                overflowY: 'auto',
                                                fontSize: '0.95rem',
                                                outline: 'none',
                                                lineHeight: 1.6,
                                                color: '#444',
                                                '&:empty:before': { content: '"Enter job description..."', color: '#999' },
                                                '& p':         { margin: 0 },
                                                '& ul, & ol':  { marginTop: 0, marginBottom: 0, paddingLeft: '20px' },
                                            }}
                                        />
                                        {/* Toolbar */}
                                        <Box sx={{ p: 0.5, display: 'flex', gap: 0.5, alignItems: 'center', borderTop: '1px solid #eee', bgcolor: '#fafafa', flexWrap: 'wrap' }}>
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleBold(); }} sx={{ color: '#666' }} title="Bold">
                                                <FormatBoldIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleItalic(); }} sx={{ color: '#666' }} title="Italic">
                                                <FormatItalicIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleUnderline(); }} sx={{ color: '#666' }} title="Underline">
                                                <FormatUnderlinedIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleStrike(); }} sx={{ color: '#666' }} title="Strikethrough">
                                                <Typography sx={{ fontSize: '13px', fontWeight: 700, textDecoration: 'line-through', lineHeight: 1 }}>S</Typography>
                                            </IconButton>
                                            <Box sx={{ width: '1px', height: '20px', bgcolor: '#ddd', mx: 0.5 }} />
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleBulletList(); }} sx={{ color: '#666' }} title="Bullet list">
                                                <FormatListBulletedIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleNumberList(); }} sx={{ color: '#666' }} title="Numbered list">
                                                <FormatListNumberedIcon fontSize="small" />
                                            </IconButton>
                                            <Box sx={{ width: '1px', height: '20px', bgcolor: '#ddd', mx: 0.5 }} />
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleSubscript(); }} sx={{ color: '#666' }} title="Subscript">
                                                <Typography sx={{ fontSize: '13px', fontWeight: 600, lineHeight: 1 }}>T<sub>2</sub></Typography>
                                            </IconButton>
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleSuperscript(); }} sx={{ color: '#666' }} title="Superscript">
                                                <Typography sx={{ fontSize: '13px', fontWeight: 600, lineHeight: 1 }}>T<sup>2</sup></Typography>
                                            </IconButton>
                                            <Box sx={{ width: '1px', height: '20px', bgcolor: '#ddd', mx: 0.5 }} />
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleAlignLeft(); }} sx={{ color: '#666' }} title="Align left">
                                                <FormatAlignLeftIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleAlignCenter(); }} sx={{ color: '#666' }} title="Align center">
                                                <FormatAlignCenterIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); handleAlignRight(); }} sx={{ color: '#666' }} title="Align right">
                                                <FormatAlignRightIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography
                                        sx={{ fontSize: '14px', lineHeight: 1.8, color: '#333' }}
                                        dangerouslySetInnerHTML={{ __html: formData.description || 'No description provided' }}
                                    />
                                )}
                            </Box>

                            {/* Benefits */}
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ color: '#7B3AB7', fontWeight: 600 }}>BENEFITS</Typography>
                                    {isEditMode && (
                                        <IconButton size="small" onClick={() => toggleSection('benefits')}>
                                            <EditOutlinedIcon sx={{ color: '#FF0000', fontSize: 18 }} />
                                        </IconButton>
                                    )}
                                </Box>
                                {editingState.benefits ? (
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            size="small"
                                            placeholder="Search benefits..."
                                            value={benefitsInputValue}
                                            onChange={(e) => setBenefitsInputValue(e.target.value)}
                                            sx={{ mt: 1, mb: 2, width: '320px', display: 'block', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            InputProps={{
                                                endAdornment: loadingBenefits ? <CircularProgress size={20} /> : null
                                            }}
                                        />
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                            {/* Selected Benefits first */}
                                            {displayBenefits.map((benefit) => (
                                                <Chip
                                                    key={`selected-${benefit.id}`}
                                                    label={benefit.name}
                                                    icon={<CheckIcon sx={{ fontSize: '16px !important', color: BRAND }} />}
                                                    onClick={() => handleLocalBenefitToggle(benefit)}
                                                    variant="filled"
                                                    sx={{
                                                        fontSize: "0.85rem",
                                                        py: 2,
                                                        px: 0.5,
                                                        borderRadius: '20px',
                                                        borderColor: BRAND,
                                                        backgroundColor: `${BRAND}15`,
                                                        color: BRAND,
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            backgroundColor: `${BRAND}25`,
                                                        }
                                                    }}
                                                />
                                            ))}
                                            {/* Filtered Unselected Benefits */}
                                            {allBenefits
                                                .filter(b => !displayBenefits.some(db => db.id === b.id))
                                                .map((benefit) => (
                                                    <Chip
                                                        key={benefit.id}
                                                        label={benefit.name}
                                                        icon={<span style={{ fontSize: "16px", marginLeft: "6px" }}>+</span>}
                                                        onClick={() => handleLocalBenefitToggle(benefit)}
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: "0.85rem",
                                                            py: 2,
                                                            px: 0.5,
                                                            borderRadius: '20px',
                                                            borderColor: '#ccc',
                                                            backgroundColor: 'transparent',
                                                            color: '#666',
                                                            fontWeight: 400,
                                                            '&:hover': {
                                                                backgroundColor: '#f5f5f5',
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {displayBenefits.length > 0 ? displayBenefits.map((benefit) => (
                                            <Chip
                                                key={benefit.id || benefit.name}
                                                label={benefit.name}
                                                variant="filled"
                                                sx={{
                                                    backgroundColor: '#D1C4E9',
                                                    color: '#000000',
                                                    borderColor: '#7B3AB7',
                                                    fontWeight: 500, fontSize: '14px',
                                                }}
                                            />
                                        )) : (
                                            <Typography sx={{ fontSize: '14px', color: '#999' }}>No benefits listed</Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Skills */}
                            <Box sx={{ mt: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ color: '#7B3AB7', fontWeight: 600 }}>SKILLS</Typography>
                                    {isEditMode && (
                                        <IconButton size="small" onClick={() => toggleSection('skills')}>
                                            <EditOutlinedIcon sx={{ color: '#FF0000', fontSize: 18 }} />
                                        </IconButton>
                                    )}
                                </Box>
                                {editingState.skills ? (
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            size="small"
                                            placeholder="Search skills..."
                                            value={skillsInputValue}
                                            onChange={(e) => setSkillsInputValue(e.target.value)}
                                            sx={{ mt: 1, mb: 2, width: '320px', display: 'block', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            InputProps={{
                                                endAdornment: loadingSkills ? <CircularProgress size={20} /> : null
                                            }}
                                        />
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                            {/* Selected Skills first */}
                                            {displaySkills.map((skill) => (
                                                <Chip
                                                    key={`selected-${skill.id}`}
                                                    label={skill.name || skill.skillName}
                                                    icon={<CheckIcon sx={{ fontSize: '16px !important', color: BRAND }} />}
                                                    onClick={() => handleLocalSkillToggle(skill)}
                                                    variant="filled"
                                                    sx={{
                                                        fontSize: "0.85rem",
                                                        py: 2,
                                                        px: 0.5,
                                                        borderRadius: '20px',
                                                        borderColor: BRAND,
                                                        backgroundColor: `${BRAND}15`,
                                                        color: BRAND,
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            backgroundColor: `${BRAND}25`,
                                                        }
                                                    }}
                                                />
                                            ))}
                                            {/* Filtered Unselected Skills */}
                                            {allSkills
                                                .filter(s => !displaySkills.some(ds => ds.id === s.id))
                                                .map((skill) => (
                                                    <Chip
                                                        key={skill.id}
                                                        label={skill.name || skill.skillName}
                                                        icon={<span style={{ fontSize: "16px", marginLeft: "6px" }}>+</span>}
                                                        onClick={() => handleLocalSkillToggle(skill)}
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: "0.85rem",
                                                            py: 2,
                                                            px: 0.5,
                                                            borderRadius: '20px',
                                                            borderColor: '#ccc',
                                                            backgroundColor: 'transparent',
                                                            color: '#666',
                                                            fontWeight: 400,
                                                            '&:hover': {
                                                                backgroundColor: '#f5f5f5',
                                                            }
                                                        }}
                                                    />
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {displaySkills.length > 0 ? displaySkills.map((skill) => (
                                            <Chip
                                                key={skill.id || skill.name}
                                                label={skill.name || skill.skillName}
                                                variant="filled"
                                                sx={{
                                                    backgroundColor: '#D1C4E9',
                                                    color: '#000000',
                                                    borderColor: '#7B3AB7',
                                                    fontWeight: 500, fontSize: '14px',
                                                }}
                                            />
                                        )) : (
                                            <Typography sx={{ fontSize: '14px', color: '#999' }}>No skills listed</Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* ── Right – Job Details ── */}
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Box sx={{
                                backgroundColor: '#F5F5FF', p: 2, px: 4, borderRadius: 2,
                                position: { xs: 'relative', md: 'sticky' }, top: { md: 20 }, alignSelf: 'flex-start',
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ color: '#7B3AB7', fontWeight: 600 }}>JOB DETAILS</Typography>
                                    {isEditMode && (
                                        <IconButton size="small" onClick={() => toggleSection('details')}>
                                            <EditOutlinedIcon sx={{ color: '#FF0000', fontSize: 18 }} />
                                        </IconButton>
                                    )}
                                </Box>

                                {editingState.details ? (
                                    <Grid container spacing={2}>
                                        {detailFields.map(({ label, field, type, options }) => (
                                            <Grid key={field} size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    fullWidth size="small" label={label}
                                                    type={type === 'number' ? 'number' : 'text'}
                                                    value={formData[field] ?? ''}
                                                    onChange={handleDetailChange(field)}
                                                    select={type === 'select'}
                                                >
                                                    {type === 'select' && (options || []).map((opt) => (
                                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <>
                                        {[
                                            { label: 'Job Title',                   value: formData.title },
                                            { label: 'Location',                    value: [formData.city, formData.area].filter(Boolean).join(', ') || 'Not specified' },
                                            { label: 'Recruitment Timeline',        value: formData.recruitmentTimeline },
                                            { label: 'No of Openings',              value: formData.numberOfPeople ?? formData.positions },
                                            { label: 'Job Type',                    value: formData.jobType || formData.employmentType },
                                            { label: 'Pay',                         value: formData.payMaximum ? `₹${formData.payMaximum}/${formData.payPeriod || ''}` : null },
                                            { label: 'Experience',                  value: formData.experience },
                                            { label: 'Work Type',                   value: formData.workType },
                                        ].map(({ label, value }) => (
                                            value !== undefined && value !== null && value !== '' ? (
                                                <Box key={label} sx={{ mb: 2 }}>
                                                    <Typography sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>{label}</Typography>
                                                    <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>{value || 'Not specified'}</Typography>
                                                </Box>
                                            ) : null
                                        ))}
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Error / Success alerts */}
                    {updateError && <Alert severity="error"   sx={{ mt: 2 }}>{updateError}</Alert>}
                    {updateSuccess && <Alert severity="success" sx={{ mt: 2 }}>{updateSuccess}</Alert>}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={onClose}
                            sx={{
                                px: 4, py: 1.5,
                                borderColor: '#7B3AB7',
                                color: '#7B3AB7',
                                fontWeight: 600,
                                borderRadius: '100px',
                                fontSize: '14px',
                                '&:hover': { borderColor: '#6a2fa0', backgroundColor: '#7B3AB708' },
                            }}
                        >
                            Close
                        </Button>
                        {isEditMode && hasChanges && (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                disabled={isSubmitting || fetchLoading}
                                sx={{
                                    px: 4, py: 1.5,
                                    backgroundColor: '#7B3AB7',
                                    fontWeight: 600,
                                    borderRadius: '100px',
                                    fontSize: '14px',
                                    '&:hover': { backgroundColor: isSubmitting ? '#7B3AB7' : '#6a2fa0' },
                                }}
                            >
                                {isSubmitting ? 'Updating...' : 'Confirm & Update'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PreviewDialogBox;